import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import ProgressSteps from '@src/elements/progress-steps/progress-steps'
import { Button } from '@src/elements/button/button'
import Checkup, { CheckupState, CheckupStep } from '@src/data/checkup'
import ApiClient from '@src/lib/api_client'
import camelcaseKeys from 'camelcase-keys'
import { API } from '@src/lib/api_path'
import { snakecaseKeys } from '@src/util/functions'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Spacer } from '@src/elements/spacer/spacer'
import Member from '@src/data/member'
import { convertToYYYYMMDD } from '@src/util/date-format'

import './onboarding-status.sass'

type CheckupResponse = { checkup: Checkup }
type MemberResponse = { member: Member }

@Component({
  computed: {
    checkup () {
      return this.$store.state.checkup
    }
  }
})
export default class MemberOnboardingStatus extends Vue {

  private checkup: Checkup

  private apiClient: ApiClient

  constructor () {
    super()
    this.apiClient = new ApiClient()
  }

  onboardingStarted (steps: CheckupStep[]): boolean {
    // the onboarding was stated if completed is defined for every step
    return steps.every(step => step.completed !== undefined)
  }

  goToOnboarding (step?: number) {
    if (!this.checkup.id) {
      // register the initial checkup (onboarding) if it does not exist yet
      return this.addNewCheckup(step)
    } else {
      this.$router.push({ path: `/trainers/onboarding/${this.$route.params.id}/${this.checkup.id}`, query: { startStep: `${step}` || /* istanbul ignore next: a template string is never empty */ '' } })
    }
  }

  addNewCheckup (step?: number) {
    const newCheckup = new Checkup({ plannedDate: convertToYYYYMMDD(new Date()), state: CheckupState.IN_PROGRESS })
    const memberId = this.$route.params.id

    return this.apiClient
      .post(API.TRAINERS_API.CHECKUPS.replace('{memberId}', memberId), { checkup: snakecaseKeys(newCheckup) })
      .then((response: CheckupResponse) => {
        let checkup = new Checkup(camelcaseKeys(response.checkup, { deep: true }))
        checkup.startOnboarding()
        this.$store.dispatch('setCheckup', checkup)
        this.$router.push({ path: `/trainers/onboarding/${memberId}/${checkup.id}`, query: { startStep: `${step}` || /* istanbul ignore next: a template string is never empty */ '' } })
        return this.setInitialCheckup(memberId, checkup.id)
      })
  }

  setInitialCheckup (memberId, checkupId) {
    // set this checkup as initial
    const member = { initialCheckupId: checkupId }
    return this.apiClient.put(`${API.TRAINERS_API.MEMBERS}/${memberId}`, { member: snakecaseKeys(member) })
  }

  async skipOnboarding () {
    let onboarding = new Checkup(this.checkup)
    onboarding.forceComplete()

    if (!onboarding.id) {
      // create the new onboarding checkup
      await this.apiClient.post(`${API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id)}`, { checkup: snakecaseKeys(onboarding) })
        .then(async (response: any) => this.completeInitialCheckup(this.$route.params.id, new Checkup(camelcaseKeys(response.checkup, { deep: true }))))
        .catch(err => console.error('Failed to update checkup, ', err))

    } else {
      // update the onboarding checkup
      await this.apiClient.put(`${API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id)}/${onboarding.id}`, { checkup: snakecaseKeys(onboarding) })
        .then(() => this.completeInitialCheckup(this.$route.params.id, onboarding))
        .catch(err => console.error('Failed to update checkup, ', err))
    }
  }

  completeInitialCheckup (memberId, checkup: Checkup) {
    // having performed the initial checkup means the user registration is complete
    const member = {
      initialCheckupId: checkup.id,
      registeredAt: new Date().toISOString()
    }
    return this.apiClient.put(`${API.TRAINERS_API.MEMBERS}/${memberId}`, { member: snakecaseKeys(member) })
      .then((response: MemberResponse) => this.$store.dispatch('setSelectedMember', new Member(camelcaseKeys(response.member))))
  }

  goToStep (step: number) {
    return this.goToOnboarding(step)
  }

  render (h: CreateElement) {
    let steps = this.checkup.getSteps()
    let isOnboardingStarted = this.onboardingStarted(steps)

    return <div class='onboarding-status-wrapper'>
      <Subtitle1 class='title'>{this.$t('PAGE.MEMBERS.ONBOARDING.STATUS')}</Subtitle1>
      <Spacer />

      <div class='onboarding-steps'>
        <ProgressSteps steps={steps} step={0} changeStepCallback={this.goToStep} />

        <div class='footer'>
          <div class='onboarding-actions'>
            <Button ref='skipOnboarding' class='skip-btn' align='right' label={this.$t('PAGE.MEMBERS.ONBOARDING.SKIP_ONBOARDING')} click={this.skipOnboarding} type='outline' />
            {isOnboardingStarted ?
              <Button ref='continueOnboarding' align='right' label={this.$t('PAGE.MEMBERS.ONBOARDING.CONTINUE')} click={this.goToOnboarding} type='elevated' />
              : <Button ref='startOnboarding' align='right' label={this.$t('PAGE.MEMBERS.ONBOARDING.START')} click={this.goToOnboarding} type='elevated' />}
          </div>
        </div>
      </div>
    </div>
  }
}
